import React, { useEffect, useState, useCallback, useContext } from 'react';
import _ from 'lodash';
import BoxedContainer from './layouts/BoxedContainer';
import { DWTCContext } from '../utils/context';
import { getTranslationByKey } from '../helpers/translation-helper';

function Card({ data }) {
  const title = _.get(data, 'content.title', '');
  const text = _.get(data, 'content.text', '');
  const file = _.get(data, 'content.file', {});
  const fileSize = _.get(data, 'content.fileSize', {});

  return (
    <div
      className="bg-[#fafafa] border col-span-12 md:col-span-6 lg:col-span-4
    p-[20px] md:p-[25px] flex gap-3 md:gap-5 border-[#CCC]">
      <a href={file?.url} target="_blank" 
      download={file?.name} className='circular-download-cta' rel="noreferrer">
        <svg
          className="h-6 w-auto lg:h-10 min-w-max"
          xmlns="http://www.w3.org/2000/svg"
          width="42"
          height="42"
          viewBox="0 0 42 42"
          fill="none">
          <path
            d="M21 23.9622L17.0489 20.0133M21 23.9622L24.9511 20.0133M21 23.9622V12.1111M29.8889
            25.9378C29.8889 26.9857 29.4726 27.9907 28.7316 28.7316C27.9907 29.4726 26.9857 29.8889
            25.9378 29.8889H16.0622C15.0143 29.8889 14.0093 29.4726 13.2684 28.7316C12.5274 27.9907
            12.1111 26.9857 12.1111 25.9378M41 21C41 32.0457 32.0457 41 21 41C9.95431 41 1 32.0457 1
            21C1 9.95431 9.95431 1 21 1C32.0457 1 41 9.95431 41 21Z"
            stroke="#CE445B"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </a>
      <div className="flex flex-col gap-y-3 md:gap-y-[15px]">
        <div className="flex flex-col gap-y-[4px] md:gap-y-4">
          <h3
            className="text-clamp12to18 leading-1.5 mb-0 md:mb-1.5 text-[#000]
            !font-DINRegular"
            dangerouslySetInnerHTML={{ __html: title }}
          />
          <span
            className="text-clamp12to15 ltr:font-URWDINRegular
          rtl:font-GESSTwoDWTCLight text-[#4A4A4A]">
            {`${_.last(_.split(file?.url, '.')).toUpperCase()} • ${fileSize || '0 Bytes'}`}
          </span>
        </div>
        {_.isEmpty(text) || (
          <p
            className="text-clamp12to16 leading-1.5 text-[#4A4A4A] ltr:font-URWDINRegular 
          rtl:font-GESSTwoDWTCLight mb-0 md:mb-3">
            {text}
          </p>
        )}
      </div>
    </div>
  );
}

export default function FreeZoneRegulationsPaginated({ id, data }) {
  const title = _.get(data, 'title', '');
  const regulationsList = _.get(data, 'regulationsList', []);
  const [shouldRender, setShouldRender] = useState(false);
  const [search, setSearch] = useState('');
  const [filteredItems, setFilteredItems] = useState(regulationsList);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const [paginatedItems, setPaginatedItems] = useState([]);
  const [totalPages, setTotalPages] = useState(0);

  const resolveFileSize = async () => {
    _.forEach(regulationsList, async (item) => {
      const fileSize = _.get(item, 'content.fileSize', '');
      _.set(item, 'content.fileSize', fileSize);
    });
  };

  const handleSearch = useCallback(
    (searchValue) => {
      if (searchValue.trim()) {
        const filtered = _.filter(regulationsList, (item) => {
          const itemTitle = _.get(item, 'content.title', '').toLowerCase();
          const itemText = _.get(item, 'content.text', '').toLowerCase();
          return (
            itemTitle.includes(searchValue.toLowerCase()) ||
            itemText.includes(searchValue.toLowerCase())
          );
        });
        setFilteredItems(filtered);
      } else {
        setFilteredItems(regulationsList);
      }
    },
    [regulationsList],
  );

  const debouncedSearch = useCallback(
    _.debounce((searchValue) => {
      handleSearch(searchValue);
    }, 300),
    [handleSearch],
  );

  const handleSearchInput = (e) => {
    setSearch(e.target.value);
    debouncedSearch(e.target.value);
  };

  useEffect(() => {
    setTotalPages(Math.ceil(filteredItems.length / itemsPerPage));
    setCurrentPage(1); // Reset to first page on items change
  }, [filteredItems, itemsPerPage]);

  useEffect(() => {
    const begin = (currentPage - 1) * itemsPerPage;
    const end = Math.min(begin + itemsPerPage, filteredItems.length);
    setPaginatedItems(filteredItems.slice(begin, end));
  }, [currentPage, filteredItems, itemsPerPage]);

  useEffect(() => {
    resolveFileSize().finally(() => setShouldRender(true));
  }, []);

  const changePage = (newPage) => {
    setCurrentPage(newPage);
  };

  const { translations } = useContext(DWTCContext);

  return (
    shouldRender && (
      <section id={id} className="relative md:mt-[17px] pb-[62px] md:pb-[104px]">
        <div className="w-full bg-desertStorm pt-10 pb-7 lg:py-12.5">
          <BoxedContainer>
            <div className="w-full flex flex-col items-center">
              <div className="flex flex-col items-center gap-6 md:gap-10 w-full">
                <div
                  className="child:text-clamp15to19 child:!font-LibreBaskervilleRegular
                  child:ltr:leading-[1.6] child:rtl:leading-[1.4] child:rtl:md:leading-[1.5] 
                  last:child:!m-0 child:text-center child:text-[#231F20]"
                  dangerouslySetInnerHTML={{ __html: title }}
                />
                <div
                  className="w-full sm:w-auto flex flex-col sm:flex-row items-center 
                gap-6 md:gap-10 lg:justify-items-center lg:justify-center">
                  <input
                    placeholder={getTranslationByKey(translations, 'Keyword or Phrase')}
                    className="py-3.5 px-5 w-full text-[#4D4D4D]
                    h-10 md:h-[55px] flex justify-center text-[13px] md:text-[15px] 
                    ltr:font-URWDINRegular rtl:font-GESSTwoDWTCLight
                    !placeholder-[#4D4D4D] 
                    rtl:text-[12px] rtl:md:text-[15px] w-[calc(100% - 160px)] sm:min-w-[460px]"
                    value={search}
                    onChange={handleSearchInput}
                  />

                  <button
                    type="button"
                    onClick={handleSearch}
                    className="py-3 pt-[10px] md:pt-4 px-12 md:px-14 transition-[.3s] bg-red 
                      hover:bg-[#B53B50] rounded-full h-8 md:h-[51px] min-h-[40px]
                      text-white uppercase tracking-widest 
                      ltr:font-URWDINBold rtl:font-GESSTwoDWTCMedium
                      w-full text-[11px] md:text-[15px]">
                    {getTranslationByKey(translations, 'Search')}
                  </button>
                </div>
              </div>
            </div>
          </BoxedContainer>
        </div>
        <div className="bg-white">
          <BoxedContainer>
            <div className="w-full h-auto flex flex-col gap-8 lg:gap-[54px] items-center">
              <div className="w-full grid grid-cols-12 gap-3 mt-7.5 lg:mt-12.5">
                {_.isEmpty(filteredItems) ? (
                  <div className="col-span-full flex items-center justify-center">
                    {getTranslationByKey(translations, 'No results found')}
                  </div>
                ) : (
                  _.map(paginatedItems, (item, key) => <Card key={key} data={item} />)
                )}
              </div>
              <div>
                <button
                  type="button"
                  onClick={() => changePage(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className={`ltr:mr-4 md:ltr:mr-8 rtl:ml-4 md:rtl:ml-8 text-[12px] 
                    md:text-[16px] lg:text-[20px] 
                    ltr:font-URWDINMedium rtl:font-GESSTwoDWTCLight text-[#4A4A4A] 
                    ${currentPage <= 1 ? 'opacity-50 pointer-events-none' : ''}`}>
                  {getTranslationByKey(translations, 'Previous')}
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    type="button"
                    key={page}
                    onClick={() => changePage(page)}
                    className={`border mx-2 w-8 md:w-10 h-8 md:h-10 ltr:font-URWDINRegular 
                      leading-[34px] md:leading-[42px] 
                      rtl:font-GESSTwoDWTCLight text-[12px] md:text-[14px] lg:text-[18px] 
                      ${
                        page === currentPage
                          ? 'bg-[#CE445B] text-white border-[#CE445B]'
                          : 'border-[#E6E6E6]'
                      }`}>
                    {page}
                  </button>
                ))}

                <button
                  type="button"
                  onClick={() => changePage(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className={`${currentPage >= totalPages ? 'opacity-50 pointer-events-none' : ''} 
                  text-[12px] md:text-[16px] lg:text-[20px]
                  ltr:font-URWDINMedium rtl:font-GESSTwoDWTCLight text-[#4A4A4A] ltr:ml-4 
                  md:ltr:ml-8 rtl:mr-4 md:rtl:mr-8`}>
                  {getTranslationByKey(translations, 'Next')}
                </button>
              </div>
            </div>
          </BoxedContainer>
        </div>
      </section>
    )
  );
}
